import { useState, useEffect, useContext } from "react";
import { Layout, Dropdown, Avatar, Badge, Popover, List, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Select, Button } from "antd";
import type { MenuProps } from "antd";
import {
  UserOutlined,
  AppstoreOutlined,
  LogoutOutlined,
  BankOutlined,
  BellOutlined,
} from "@ant-design/icons";

import { AuthContext } from "../../context/AuthContext";
import { TourContext } from "../../context/TourContext";

import "../../styles/TopBar.css";
import { getGroupApi, getUserGroupsApi } from "../../services/Group/GroupApis";
import { 
  getNotificationsApi, 
  markNotificationAsReadApi, 
  markAllNotificationsAsReadApi 
} from "../../services/Notification/NotificationApis";
import AdminIssueBadges from "./AdminIssueBadges";

const { Header } = Layout;

const items: MenuProps["items"] = [
  // {
  //   label: (
  //     <>
  //       <UserOutlined /> Profile
  //     </>
  //   ),
  //   key: "1",
  // },
  // {
  //   type: "divider",
  // },
  {
    label: (
      <>
        <AppstoreOutlined /> Group
      </>
    ),
    key: "2",
  },
  {
    type: "divider",
  },
  {
    label: (
      <>
        <LogoutOutlined /> Logout
      </>
    ),
    key: "3",
  },
];

const TopBar: React.FC = () => {
  const { currentUser, currentGroup, setCurrentGroup, logout } =
    useContext(AuthContext);
  const { refActiveGroup, refOptions, markPageAsVisited, pages } =
    useContext(TourContext);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<any>({});
  const [options, setOptions] = useState([{ value: "", label: "" }]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [useBackendAPI, setUseBackendAPI] = useState(true); // Toggle when backend is ready

  const unreadCount = notifications.filter(n => !n.read).length;

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    if (!currentUser?.id || !useBackendAPI) return; // Skip if backend not ready
    
    setLoadingNotifications(true);
    try {
      const response = await getNotificationsApi(currentUser.id);
      if (response?.data && Array.isArray(response.data)) {
        setNotifications(response.data);
      }
    } catch (error: any) {
      console.error("Error fetching notifications:", error);
      // Keep using fallback data if API fails
      if (error?.response?.status === 404) {
        console.log("Notification API not yet implemented, using sample data");
        setUseBackendAPI(false); // Disable further API calls
      }
    } finally {
      setLoadingNotifications(false);
    }
  };

  const navigate = useNavigate();

  // Mark single notification as read
  const markAsRead = async (id: number) => {
    // Update locally first for immediate feedback
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
    
    // Only call backend if enabled
    if (!useBackendAPI) return;
    
    try {
      await markNotificationAsReadApi(id);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    // Update locally first for immediate feedback
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    
    // Only call backend if enabled
    if (!currentUser?.id || !useBackendAPI) return;
    
    try {
      await markAllNotificationsAsReadApi(currentUser.id);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const showModal = () => {
    setIsModalOpen(true);

    setOptions(
      currentUser.Group.map((group: any) => {
        return {
          value: group.id,
          label: group.GroupName,
        };
      })
    );
  };

  const handleOk = (e: any) => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onChange = (value: string) => {
    getGroupApi(value)
      .then((res) => {
        localStorage.setItem("CurrentGroupID", res.data.id);
        setCurrentGroup(res.data.id);
        setSelected({ value: res.data.id, label: res.data.GroupName });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onSearch = (e: any) => {
    const value = e;

    if (value !== "") {
      const filteredData = currentUser.Group.filter((row: any) => {
        return Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(value.toLowerCase());
      });

      const filteredOptions = filteredData.map((group: any) => {
        return {
          value: group.id,
          label: group.GroupName,
        };
      });

      setOptions(filteredOptions);
    }
  };

  const onClick: MenuProps["onClick"] = ({ key }) => {
    switch (key) {
      case "1":
        navigate("#");
        break;
      case "2":
        showModal();
        break;
      case "3":
        logout();
        break;
    }
  };

  useEffect(() => {
    if (currentGroup) {
      getGroupApi(currentGroup)
        .then((res) => {
          setSelected({ value: res.data.id, label: res.data.GroupName });
        })
        .catch((err) => {
          console.log(err);
        });
    }
    
    // Only fetch notifications if backend API is enabled
    if (useBackendAPI) {
      fetchNotifications();
      
      // Poll for new notifications every 30 seconds
      const notificationInterval = setInterval(() => {
        fetchNotifications();
      }, 30000); // 30 seconds
      
      return () => clearInterval(notificationInterval);
    }
  }, [currentUser, currentGroup, useBackendAPI]);

  const notificationContent = (
    <div style={{ width: 320, maxHeight: 400, overflow: 'auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '8px 16px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Typography.Text strong>Notifications</Typography.Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={markAllAsRead}>
            Mark all as read
          </Button>
        )}
      </div>
      <List
        dataSource={notifications}
        locale={{ emptyText: 'No notifications' }}
        renderItem={(item) => (
          <List.Item
            style={{ 
              backgroundColor: item.read ? 'transparent' : '#f0f5ff',
              cursor: 'pointer',
              padding: '12px 16px',
              borderBottom: '1px solid #f0f0f0'
            }}
            onClick={() => markAsRead(item.id)}
          >
            <List.Item.Meta
              title={
                <Typography.Text strong={!item.read}>
                  {item.title}
                </Typography.Text>
              }
              description={
                <>
                  <Typography.Text style={{ fontSize: 13, color: '#595959' }}>
                    {item.message}
                  </Typography.Text>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {item.timestamp}
                  </Typography.Text>
                </>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <>
      <Header
        style={{
          padding: "5px 16px",
          lineHeight: "normal",
          position: "sticky",
          top: 0,
          zIndex: 9,
        }}
        className="header"
      >
        <div className="logo">
          <BankOutlined style={{ color: "#f04f05", paddingRight: "6px" }} />
          <h3 ref={refActiveGroup}>{selected.label}</h3>
        </div>

        <div className="avatar" style={{ gap: '16px', display: 'flex', alignItems: 'center' }}>
          {/* <Popover
            content={notificationContent}
            title={null}
            trigger="click"
            open={notificationOpen}
            onOpenChange={setNotificationOpen}
            placement="bottomRight"
          >
            <Badge count={unreadCount} size="small">
              <BellOutlined 
                style={{ 
                  fontSize: 18, 
                  color: '#595959',
                  cursor: 'pointer',
                  padding: '4px'
                }} 
              />
            </Badge>
          </Popover> */}
          <AdminIssueBadges
            maintenanceEnabled={
              currentUser?.Role?.Name === "Super Admin"
            }
            complianceEnabled={
              currentUser?.Role?.Name === "Admin" ||
              currentUser?.Role?.Name === "Super Admin"
            }
            maintenancePath="/maintenance"
            compliancePath="/compliance"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Dropdown menu={{ items, onClick }}>
              <div ref={refOptions} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <Avatar
                  style={{ backgroundColor: "#f04f05" }}
                  icon={<UserOutlined />}
                />
                <div className="user-meta">
                  <p>
                    <strong>{currentUser ? currentUser.Name : ""}</strong>
                  </p>
                  <p style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    {currentUser ? currentUser.Role.Name : ""}
                  </p>
                </div>
              </div>
            </Dropdown>
          </div>
        </div>
      </Header>

      <Modal
        title="Select Group"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
      >
        <Select
          defaultValue={selected}
          size="large"
          style={{ width: "50%", marginRight: "25%" }}
          showSearch
          placeholder="Select group"
          optionFilterProp="label"
          onChange={onChange}
          onSearch={onSearch}
          options={options}
        />
        <Button size="large" onClick={handleOk} style={{ width: "25%" }}>
          Okay
        </Button>
      </Modal>
    </>
  );
};

export default TopBar;
